import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import {
  Sparkles, Check, ArrowRight, Users, Award, MessageCircle,
  Calendar, BookOpen, DollarSign, Car, Clock, Shield, GraduationCap,
  Heart, Star, Loader2,
} from "lucide-react";
import EditableText from "../components/EditableText";
import PreviewModeToggle from "../components/PreviewModeToggle";
import PageBlocks from "../components/PageBlocks";
import FoundingSpotsCounter from "../components/FoundingSpotsCounter";

/**
 * BetaLaunchLanding — full launch-style experience for visitors who've just
 * redeemed a valid beta invite code. Identical to what the public will see at
 * official launch (same design / copy / pricing), with a prominent founding-
 * member banner showing the $19/$199 locked-for-life RDH Pro rate.
 *
 * All 4 tier cards are clickable per spec; whichever the beta tester picks,
 * the backend forces RDH Pro at the founding rate (handled in /payments/subscribe).
 */
const TIER_ORDER = ["student", "community", "hygienist", "pro"];

const FEATURE_LIST = [
  { icon: Users, title: "Verified RDH community", desc: "Every member is a licence-verified registered dental hygienist. No imposters, no recruiters." },
  { icon: MessageCircle, title: "Discussions & mentorship", desc: "Province-wide conversations, anonymous when needed. Get paired with experienced mentors." },
  { icon: DollarSign, title: "Real-time wage transparency", desc: "Anonymous wage surveys across Canada. See exactly what other RDHs in your province are earning." },
  { icon: Award, title: "CE Tracker", desc: "Log courses, store certificates, get reminders when renewal is due. Province-specific requirements." },
  { icon: Car, title: "Mileage & shift trackers", desc: "CRA-compliant logs with PDF exports. Auto-categorized expenses using the latest tax codes." },
  { icon: Heart, title: "Health & burnout tools", desc: "Daily check-ins, burnout assessments, breathing exercises. Your career sustainability matters." },
  { icon: Calendar, title: "Local meetups & events", desc: "In-person hangouts, CE workshops, study sessions near you. RSVP and connect IRL." },
  { icon: BookOpen, title: "Curated resources", desc: "Practice standards from every Canadian regulator. New-grad onboarding, clinical references, business tools." },
  { icon: Star, title: "Clinic ratings", desc: "Anonymous reviews of practices across Canada. Find offices with respectful management and fair pay." },
];

const BetaLaunchLanding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plansData, setPlansData] = useState(null);
  const [tierMatrix, setTierMatrix] = useState({ matrix: {}, catalog: [] });
  const [billingInterval, setBillingInterval] = useState("annual"); // default to annual — better value
  const [submitting, setSubmitting] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/payments/plans`).then(r => setPlansData(r.data)).catch(() => {}).finally(() => setLoading(false));
    // Pull the live tier→feature matrix so the cards under each tier reflect
    // exactly what the founder has enabled in Manage Tiers.
    axios.get(`${API}/tiers/features/public`).then(r => setTierMatrix(r.data)).catch(() => {});
  }, []);

  // Beta tester rate (always Pro tier at founding price)
  const BETA_RATE = { monthly: 19, annual: 199 };

  const handleGetStarted = async (tierKeyClicked) => {
    setSubmitting(tierKeyClicked);
    // If they're not logged in, send to register and remember intent.
    if (!user) {
      sessionStorage.setItem("dhdh_beta_intent", JSON.stringify({
        tier: tierKeyClicked,
        interval: billingInterval,
      }));
      navigate("/register");
      return;
    }
    // If they're logged in, kick off Stripe checkout. Backend forces Pro
    // tier + founding rate for beta testers regardless of what tier they pick.
    try {
      const planKey = `${tierKeyClicked}_${billingInterval}`;
      const res = await axios.post(`${API}/payments/subscribe`, {
        plan: planKey,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url || res.data.url) {
        window.location.href = res.data.checkout_url || res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start checkout");
      setSubmitting(null);
    }
  };

  const tiers = plansData?.tiers || {};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <Loader2 className="w-8 h-8 animate-spin text-[#6B4E8A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF7F2] via-white to-[#F0ECF7]" data-testid="beta-launch-page">
      {/* Founder-managed text injected at top */}
      <PageBlocks pageKey="beta_launch" placement="top" className="max-w-5xl mx-auto px-6 pt-6" />
      {/* Hero */}
      <header className="relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-[#8DB5A0]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#6B4E8A]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6 pt-16 pb-12 text-center">
          {/* Beta unlock banner */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-[#6B4E8A] text-white shadow-sm"
            data-testid="beta-unlocked-badge"
          >
            <Sparkles className="w-4 h-4" />
            <EditableText
              blockKey="beta_launch.unlocked_badge"
              fallback="You're in — Beta Founding Member access"
              as="span"
              className="text-xs font-medium tracking-wide uppercase"
            />
          </motion.div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#6B4E8A] mb-6 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            <EditableText
              blockKey="beta_launch.hero_headline_lead"
              fallback="Welcome to"
              as="span"
            />{" "}
            <EditableText
              blockKey="beta_launch.hero_headline_accent"
              fallback="DHDH"
              as="span"
              className="text-[#8DB5A0]"
            />
            <EditableText
              blockKey="beta_launch.hero_headline_tail"
              fallback="."
              as="span"
            />
          </h1>
          <EditableText
            blockKey="beta_launch.hero_subhead"
            fallback="The first private, verified-only platform built for Canadian registered dental hygienists. Discussions, mentorship, wage transparency, CE tracking, shift tools and resources — all in one place."
            as="p"
            className="text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed"
            multiline
          />
        </div>
      </header>

      {/* Founding member callout */}
      <section className="px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl mx-auto bg-gradient-to-br from-[#6B4E8A] to-[#553D6F] text-white rounded-3xl p-8 sm:p-10 shadow-lg"
          data-testid="founding-rate-callout"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
              <Award className="w-6 h-6 text-[#8DB5A0]" />
            </div>
            <div>
              <EditableText
                blockKey="beta_launch.founding_eyebrow"
                fallback="Your founding-member offer"
                as="p"
                className="text-xs uppercase tracking-widest text-[#8DB5A0] mb-1"
              />
              <EditableText
                blockKey="beta_launch.founding_title"
                fallback="Full RDH Pro features — locked for life."
                as="h2"
                className="text-2xl sm:text-3xl font-medium mb-3"
                style={{ fontFamily: "Outfit, sans-serif" }}
              />
              <EditableText
                blockKey="beta_launch.founding_body"
                fallback="As a beta founding member, you get every feature on DHDH at $19/month or $199/year, locked for life. No price hikes — ever — as long as your membership stays active."
                as="p"
                className="text-white/85 mb-6 leading-relaxed text-sm sm:text-base"
                multiline
              />
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <div className="px-4 py-2 bg-white/10 rounded-full">
                  <span className="text-white/60 line-through mr-2">$29/mo</span>
                  <span className="font-medium text-white">$19/mo</span>
                </div>
                <div className="px-4 py-2 bg-white/10 rounded-full">
                  <span className="text-white/60 line-through mr-2">$249/yr</span>
                  <span className="font-medium text-white">$199/yr</span>
                </div>
                <span className="text-white/70 text-xs">Save $50 with annual</span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Feature list */}
      <section className="px-6 py-12">
        <div className="max-w-5xl mx-auto">
          <EditableText
            blockKey="beta_launch.features_title"
            fallback="Everything you get"
            as="h2"
            className="text-2xl sm:text-3xl font-medium text-[#6B4E8A] text-center mb-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          />
          <EditableText
            blockKey="beta_launch.features_subtitle"
            fallback="Built for Canadian RDHs. Designed for real practice, not marketing slides."
            as="p"
            className="text-[#64748B] text-center mb-10 text-sm"
            multiline
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURE_LIST.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white border border-[#E5DFEC] rounded-2xl p-5"
                  data-testid={`feature-${i}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-[#F0ECF7] flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-[#6B4E8A]" />
                  </div>
                  <h3 className="font-medium text-[#6B4E8A] mb-1.5 text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {f.title}
                  </h3>
                  <p className="text-xs text-[#64748B] leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing — 4 clickable tiers; backend forces Pro at beta rate */}
      <section className="px-6 py-12 bg-gradient-to-b from-transparent to-[#F0ECF7]/40">
        <div className="max-w-6xl mx-auto">
          <EditableText
            blockKey="beta_launch.pricing_title"
            fallback="Public launch pricing"
            as="h2"
            className="text-2xl sm:text-3xl font-medium text-[#6B4E8A] text-center mb-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          />
          <p className="text-[#64748B] text-center mb-3 text-sm max-w-xl mx-auto">
            Pick any tier — as a beta founding member you'll <strong className="text-[#6B4E8A]">lock it in for life</strong>, immune to all future price increases. Pick <strong className="text-[#6B4E8A]">RDH Pro</strong> for the special founding rate of <strong className="text-[#6B4E8A]">$19/mo or $199/yr</strong>.
          </p>

          {/* Monthly/Annual toggle */}
          <div className="flex justify-center mb-8" data-testid="billing-toggle">
            <div className="inline-flex rounded-full bg-white border border-[#E5DFEC] p-1">
              {["monthly", "annual"].map((opt) => (
                <button
                  key={opt}
                  onClick={() => setBillingInterval(opt)}
                  className={`px-5 py-2 rounded-full text-xs font-medium transition-all ${
                    billingInterval === opt
                      ? "bg-[#6B4E8A] text-white shadow-sm"
                      : "text-[#64748B] hover:text-[#6B4E8A]"
                  }`}
                  data-testid={`billing-${opt}`}
                >
                  {opt === "monthly" ? "Monthly" : "Annual"}
                  {opt === "annual" && <span className="ml-1.5 text-[10px] uppercase tracking-wider opacity-80">Save more</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Founding-Member Spots Counter */}
          <FoundingSpotsCounter variant="pricing" className="max-w-2xl mx-auto mb-8" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TIER_ORDER.map((tierKey, i) => {
              const tier = tiers[tierKey] || {};
              const monthly = tier.monthly || 0;
              const annual = tier.annual || 0;
              const displayPrice = billingInterval === "monthly" ? monthly : annual;
              const suffix = billingInterval === "monthly" ? "/mo" : "/yr";
              const tierLabel = tier.label || tierKey;
              const tierTagline = tier.tagline || "";
              const isPro = tierKey === "pro";
              const annualSavings = monthly * 12 - annual;

              return (
                <motion.div
                  key={tierKey}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.07 }}
                  className={`relative rounded-2xl p-6 border ${
                    isPro
                      ? "bg-[#6B4E8A] border-[#6B4E8A] text-white shadow-lg lg:scale-105"
                      : "bg-white border-[#E5DFEC] text-[#6B4E8A]"
                  }`}
                  data-testid={`tier-card-${tierKey}`}
                >
                  {isPro && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#8DB5A0] text-white text-[10px] font-medium uppercase tracking-wider rounded-full">
                      Beta founding rate
                    </div>
                  )}
                  <h3 className="font-medium text-base mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {tierLabel}
                  </h3>
                  <p className={`text-xs mb-4 min-h-[40px] leading-relaxed ${isPro ? "text-white/70" : "text-[#64748B]"}`}>
                    {tierTagline}
                  </p>

                  {isPro ? (
                    <div className="mb-5">
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-medium">
                          ${billingInterval === "monthly" ? BETA_RATE.monthly : BETA_RATE.annual}
                        </span>
                        <span className="text-sm opacity-80">{suffix}</span>
                      </div>
                      <p className="text-[11px] mt-1 text-white/65">
                        <span className="line-through opacity-80">${displayPrice}{suffix}</span>
                        <span className="ml-1.5">→ your beta rate, locked for life</span>
                      </p>
                    </div>
                  ) : (
                    <div className="mb-5">
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-medium">${displayPrice}</span>
                        <span className="text-sm opacity-80">{suffix}</span>
                      </div>
                      <p className="text-[11px] text-[#8DB5A0] mt-1">
                        Locked for life as a beta founding member
                        {billingInterval === "annual" && annualSavings > 0 && (
                          <span className="text-[#94A3B8]"> · save ${annualSavings.toFixed(0)}/yr</span>
                        )}
                      </p>
                    </div>
                  )}

                  <Button
                    onClick={() => handleGetStarted(tierKey)}
                    disabled={submitting === tierKey}
                    className={`w-full rounded-full ${
                      isPro
                        ? "bg-white text-[#6B4E8A] hover:bg-[#FAF7F2]"
                        : "bg-[#6B4E8A] text-white hover:bg-[#553D6F]"
                    }`}
                    data-testid={`get-started-${tierKey}`}
                  >
                    {submitting === tierKey ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>Get Started <ArrowRight className="w-4 h-4 ml-1.5" /></>
                    )}
                  </Button>
                  <p className={`text-[10px] mt-2 text-center ${isPro ? "text-white/60" : "text-[#94A3B8]"}`}>
                    {isPro
                      ? `You'll pay $${billingInterval === "monthly" ? BETA_RATE.monthly : BETA_RATE.annual}${suffix} (beta founding rate)`
                      : `Your $${displayPrice}${suffix} is locked for life`}
                  </p>

                  {/* Live tier-feature list — pulled from /api/tiers/features/public
                      so flipping a feature in Manage Tiers updates this card on next load. */}
                  {(() => {
                    const enabledKeys = Object.entries(tierMatrix.matrix?.[tierKey] || {})
                      .filter(([, on]) => on)
                      .map(([k]) => k);
                    if (enabledKeys.length === 0) return null;
                    const labelMap = Object.fromEntries((tierMatrix.catalog || []).map(f => [f.key, f.label]));
                    return (
                      <ul
                        className={`mt-4 pt-4 border-t ${isPro ? "border-white/20" : "border-[#E5DFEC]"} space-y-1.5 text-left`}
                        data-testid={`tier-features-${tierKey}`}
                      >
                        {enabledKeys.map((fk) => (
                          <li
                            key={fk}
                            className={`flex items-start gap-1.5 text-[11px] ${isPro ? "text-white/85" : "text-[#475569]"}`}
                          >
                            <Check className={`w-3 h-3 mt-0.5 shrink-0 ${isPro ? "text-[#8DB5A0]" : "text-[#10B981]"}`} />
                            <span>{labelMap[fk] || fk.replace(/_/g, " ")}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </motion.div>
              );
            })}
          </div>

          {/* Trust + trial info */}
          <div className="mt-10 max-w-2xl mx-auto bg-white border border-[#E5DFEC] rounded-2xl p-5 text-center">
            <div className="flex items-center justify-center gap-2 mb-2 text-[#6B4E8A]">
              <Shield className="w-4 h-4 text-[#8DB5A0]" />
              <EditableText
                blockKey="beta_launch.trial_title"
                fallback="12 months free for beta founding members"
                as="p"
                className="text-sm font-medium"
                style={{ fontFamily: "Outfit, sans-serif" }}
              />
            </div>
            <EditableText
              blockKey="beta_launch.trial_body"
              fallback="After you sign up and add your payment method, your 12-month free trial begins the moment an admin verifies your Photo ID + Practice Permit. No charges for a full year. Cancel anytime during the trial."
              as="p"
              className="text-xs text-[#64748B] leading-relaxed"
              multiline
            />
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-3 text-[11px] text-[#94A3B8]">
              <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-[#10B981]" /> Cancel anytime</span>
              <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-[#10B981]" /> Canadian-hosted data</span>
              <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-[#10B981]" /> PIPEDA / PHIPA compliant</span>
            </div>
          </div>
        </div>
      </section>

      {/* Founder-managed text injected mid-page (between hero and footer) */}
      <PageBlocks pageKey="beta_launch" placement="middle" className="max-w-5xl mx-auto px-6 my-10" />

      {/* Footer CTA */}
      <footer className="px-6 py-12 text-center">
        <p className="text-xs text-[#94A3B8]">
          Already signed up? <button onClick={() => navigate("/login")} className="text-[#6B4E8A] hover:underline" data-testid="login-link">Log in here</button>.
        </p>
        <div className="mt-4 flex gap-4 justify-center flex-wrap text-xs text-[#94A3B8]">
          <a href="/privacy" className="hover:text-[#6B4E8A]" data-testid="footer-link-privacy">Privacy</a>
          <a href="/terms" className="hover:text-[#6B4E8A]" data-testid="footer-link-terms">Terms</a>
          <a href="/cookie-policy" className="hover:text-[#6B4E8A]" data-testid="footer-link-cookie">Cookie Policy</a>
          <a href="/disclaimer" className="hover:text-[#6B4E8A]" data-testid="footer-link-disclaimer">Disclaimer</a>
        </div>
      </footer>

      {/* Founder-managed text at bottom of page */}
      <PageBlocks pageKey="beta_launch" placement="bottom" className="max-w-5xl mx-auto px-6 pb-12" />

      {/* Founder preview-mode toggle (renders only for founder/master-admin) */}
      <PreviewModeToggle />
    </div>
  );
};

export default BetaLaunchLanding;
